import { useMutation, useQueryClient, type DefaultError } from "@tanstack/react-query"
import { useDispatch } from "react-redux"
import { useGetQuery } from "hooks/queries"
import { Creators } from "model/apiWrapper"
import { useNotifications } from "hooks/notifications"
import { useAccountAlias } from "resources/account"
import type { PatListResponse, PatMutationResponse, PatType, PersonalAccessToken } from "../types"

const PAT_KEY_ROOT = "personal-access-tokens"

const EMPTY_TOKENS: PersonalAccessToken[] = []

interface ApiErrorLike {
  errors?: string | string[]
  response?: string
  code?: number
}

// A bare 403 (ForbiddenError, src/new/api/errors.js) carries no body — the fetch layer
// (src/new/api/utils.ts) throws it before reading the response, discarding whatever the
// server said. It covers two distinct causes (PATs disabled for the account, or the user
// lacking write entitlement anywhere) that are indistinguishable from the client, so the
// message must stay honest about neither cause specifically.
const FORBIDDEN_MESSAGE = "This action isn't allowed for this account."

function isForbidden(error: ApiErrorLike): boolean {
  return error.code === 403
}

function post<T>(dispatch: ReturnType<typeof useDispatch>, url: string, payload?: object): Promise<T> {
  return new Promise((resolve, reject) => {
    dispatch(Creators.post(url, payload, resolve, reject))
  })
}

function del<T>(dispatch: ReturnType<typeof useDispatch>, url: string): Promise<T> {
  return new Promise((resolve, reject) => {
    dispatch(Creators.delete(url, undefined, resolve, reject))
  })
}

export function getPatErrorMessage(error: unknown): string {
  const fallback = "Something went wrong. Please try again."
  if (typeof error !== "object" || error === null) return fallback
  const apiError = error as ApiErrorLike
  const { errors, response } = apiError
  if (Array.isArray(errors) && errors.every((entry): entry is string => typeof entry === "string")) {
    return errors.join(", ")
  }
  if (typeof errors === "string") return errors
  if (typeof response === "string") return response
  if (isForbidden(apiError)) return FORBIDDEN_MESSAGE
  return fallback
}

export function usePat() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()
  const account = useAccountAlias()

  // The account governs whether PATs may be used at all, so it rides along on
  // every call and scopes the cache — two accounts must not share a response.
  const suffix = `?account=${encodeURIComponent(account)}`
  const patKey = [PAT_KEY_ROOT, account]

  // No account means no answer to "may this member use PATs here?", so don't ask —
  // an unguarded fetch would request `?account=undefined` and cache it under that key.
  // The profile only mounts inside a resolved account today, but that invariant lives
  // three components away in account-root, so don't depend on it from here.
  const query = useGetQuery<PatListResponse>({
    url: `/api/user/pat${suffix}`,
    key: patKey,
    enabled: !!account,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: patKey })

  // A 403 here means the server-side state (account PAT setting or the caller's write
  // entitlement) no longer matches what the client last fetched. Re-fetching lets
  // `patEnabled`/`canCreateReadWrite` come back current so the card self-heals (e.g. hides
  // itself) instead of leaving stale buttons that fail identically on every click until the
  // member navigates away and back. Other transient errors don't imply stale server state,
  // so they're left alone.
  const onMutationError = (error: unknown) => {
    notifs.error(getPatErrorMessage(error))
    if (isForbidden(error as ApiErrorLike)) invalidate()
  }

  const createMutation = useMutation<PatMutationResponse, DefaultError, PatType>({
    mutationFn: type => post<PatMutationResponse>(dispatch, `/api/user/pat${suffix}`, { type }),
    onSuccess: invalidate,
    onError: onMutationError,
  })

  const revealMutation = useMutation<{ secret: string }, DefaultError, string>({
    mutationFn: id => post<{ secret: string }>(dispatch, `/api/user/pat/${id}/reveal${suffix}`),
    onError: onMutationError,
  })

  const rotateMutation = useMutation<PatMutationResponse, DefaultError, string>({
    mutationFn: id => post<PatMutationResponse>(dispatch, `/api/user/pat/${id}/rotate${suffix}`),
    onSuccess: invalidate,
    onError: onMutationError,
  })

  const revokeMutation = useMutation<unknown, DefaultError, string>({
    mutationFn: id => del(dispatch, `/api/user/pat/${id}${suffix}`),
    onSuccess: invalidate,
    onError: onMutationError,
  })

  return {
    tokens: query.data?.tokens ?? EMPTY_TOKENS,
    canCreateReadWrite: query.data?.canCreateReadWrite ?? false,
    // True whenever there is no response to read (in flight, or failed) so the card survives
    // to show its error and Retry — see the visibility gate in components/personal-access-tokens.
    // Once a response exists, an absent or unexpected flag reads as disabled: the same
    // fail-closed polarity the admin card uses, so the two readers cannot disagree about
    // whether the account allows PATs.
    patEnabled: query.data ? query.data.patEnabled === true : true,
    // `isPending` rather than `isLoading` so a query that hasn't started (no account yet)
    // reads the same as one in flight. `isLoading` is false while disabled, which would let
    // the card render its empty state with live Create buttons before we know the answer.
    isPending: query.isPending,
    isError: query.isError,
    isMutating:
      createMutation.isPending
      || revealMutation.isPending
      || rotateMutation.isPending
      || revokeMutation.isPending,
    refetch: query.refetch,
    createToken: createMutation.mutateAsync,
    revealToken: revealMutation.mutateAsync,
    rotateToken: rotateMutation.mutateAsync,
    revokeToken: revokeMutation.mutateAsync,
  }
}

export default usePat
